import React, { useEffect } from 'react';
import { motion, useAnimation } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import person1 from './../../assets/person1.jpg';
import person2 from './../../assets/person2.jpg';
import Header from "../../components/header";

const Testimonials = () => {
  const controls = useAnimation();
  const [ref, inView] = useInView({
    threshold: 0.1,
    triggerOnce: false
  });

  useEffect(() => {
    if (inView) {
      controls.start('visible');
    } else {
      controls.start('hidden');
    }
  }, [controls, inView]);

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.3,
        delayChildren: 0.2
      }
    }
  };

  const itemVariants = {
    hidden: { y: 20, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1,
      transition: {
        duration: 0.5,
        ease: [0.3, 0.1, 0.3, 1]
      }
    }
  };

  const cardVariants = {
    hidden: { scale: 0.9, opacity: 0 },
    visible: {
      scale: 1,
      opacity: 1,
      transition: {
        duration: 0.5,
        ease: [0.3, 0.1, 0.3, 1]
      }
    },
    hover: {
      y: -10,
      boxShadow: "0 15px 30px rgba(0, 0, 0, 0.1)",
      transition: {
        duration: 0.3
      }
    }
  };

  return (
    <>
      <section className="testimonials-section" ref={ref}>
        <Header/>
        <br />
        <div className="testimonials-content">
          <motion.div 
            className="testimonials-text"
            initial="hidden"
            animate={controls}
            variants={containerVariants}
          >
            <motion.h1 variants={itemVariants} className="section-title">
              What Our Clients Say
            </motion.h1>
            <motion.p variants={itemVariants} className="section-subtitle">
              Testimonials and Reviews
            </motion.p>
          </motion.div>
          
          <motion.div 
            className="testimonials-cards"
            initial="hidden"
            animate={controls}
            variants={containerVariants}
          >
            <motion.div 
              className="testimonials-card"
              variants={cardVariants}
              whileHover="hover"
            >
              <div className="testimonial-image-container">
                <img src={person1} alt="John Doe" className="testimonial-image" />
                <div className="quote-icon">"</div>
              </div>
              <p>
                "Mabunda Tech has been a game-changer for our business. Their mobile app development services are top-notch and their team is very professional."
              </p>
              <h3>Dineo Leshabane</h3>
              <p className="testimonial-role">CEO, Didi's Beuaty Parlour</p>
            </motion.div>
            
            <motion.div 
              className="testimonials-card"
              variants={cardVariants}
              whileHover="hover"
            >
              <div className="testimonial-image-container">
                <img src={person2} alt="Jane Smith" className="testimonial-image" />
                <div className="quote-icon">"</div>
              </div>
              <p>
                "We were blown away by the quality of work delivered by Mabunda Tech. Their web development services are exceptional and we highly recommend them."
              </p>
              <h3>Hlayiseka Mabunda</h3>
              <p className="testimonial-role">CTO, FlairConcepts</p>
            </motion.div>
          </motion.div>
        </div>
      </section>

      <style jsx>{`
        .testimonials-section {
          padding: 100px 0;
          background-color: #ffffff;
          position: relative;
          overflow: hidden;
        }

        .testimonials-content {
          max-width: 1200px;
          margin: 0 auto;
          padding: 0 20px;
        }

        .testimonials-text {
          margin-bottom: 60px;
          text-align: center;
        }

        .section-title {
          color: #2c3e50;
          font-size: 2.8rem;
          font-weight: 700;
          margin-bottom: 15px;
          background: linear-gradient(90deg, #3498db, #2c3e50);
          -webkit-background-clip: text;
          -webkit-text-fill-color: transparent;
        }

        .section-subtitle {
          color: #7f8c8d;
          font-size: 1.2rem;
          font-weight: 400;
          letter-spacing: 1px;
        }

        .testimonials-cards {
          display: flex;
          flex-wrap: wrap;
          justify-content: center;
          gap: 30px;
        }

        .testimonials-card {
          background-color: #ffffff;
          padding: 40px 30px;
          border-radius: 15px;
          box-shadow: 0 5px 15px rgba(0, 0, 0, 0.05);
          width: calc(50% - 30px);
          text-align: center;
          border: 1px solid #f0f0f0;
          transition: all 0.3s ease;
          position: relative;
          overflow: hidden;
        }

        .testimonials-card::before {
          content: '';
          position: absolute;
          top: 0;
          left: 0;
          width: 100%;
          height: 5px;
          background: linear-gradient(90deg, #3498db, #2c3e50);
        }

        .testimonial-image-container {
          position: relative;
          margin: 0 auto 20px;
          width: 90px;
          height: 90px;
        }

        .testimonial-image {
          width: 100%;
          height: 100%;
          border-radius: 50%;
          object-fit: cover;
          border: 3px solid #f0f0f0;
          box-shadow: 0 5px 15px rgba(0, 0, 0, 0.1);
        }

        .quote-icon {
          position: absolute;
          top: -15px;
          right: -10px;
          font-size: 4rem;
          color: #3498db;
          opacity: 0.2;
          font-family: serif;
          font-weight: bold;
        }

        .testimonials-card p {
          font-size: 1.1rem;
          line-height: 1.8;
          color: #555;
          margin-bottom: 25px;
          position: relative;
          z-index: 1;
        }

        .testimonials-card h3 {
          font-size: 1.3rem;
          color: #2c3e50;
          margin-bottom: 5px;
          font-weight: 600;
        }

        .testimonial-role {
          color: #7f8c8d;
          font-size: 0.9rem;
          margin-top: 0;
        }

        /* Responsive styles */
        @media (max-width: 992px) {
          .testimonials-card {
            width: calc(50% - 20px);
          }
        }

        @media (max-width: 768px) {
          .testimonials-section {
            padding: 80px 0;
          }
          
          .testimonials-card {
            width: 100%;
            max-width: 500px;
            margin: 0 auto;
          }
          
          .section-title {
            font-size: 2.2rem;
          }
          
          .section-subtitle {
            font-size: 1rem;
          }
        }

        @media (max-width: 480px) {
          .testimonials-section {
            padding: 60px 0;
          }
          
          .section-title {
            font-size: 1.8rem;
          }
          
          .testimonials-card {
            padding: 30px 20px;
          }
          
          .testimonials-card p {
            font-size: 1rem;
          }
        }
      `}</style>
    </>
  );
};

export default Testimonials;